import { Avatar, Box, Stack, Typography } from '@mui/material'
import { CollapsableRow, Column, Row } from '../../store/types/utils/DataTableUtils'
import { t } from 'i18next'
import { useGetCompletedSettlementsQuery } from '../../store/redux/reducers/workforceScheduleSlice'
import TableCell from '@mui/material/TableCell'
import TransitionTableCell from '../../components/Table/CollapsibleTable/TransitionTableCell'
import React, { ReactNode, useMemo } from 'react'
import CollapsibleTable from '../../components/Table/CollapsibleTable/CollapsibleTable'
import SettlementResponse from '../../store/types/response/SettlementResponse'
import { Settlement, WorkEntry } from '../../store/types'
import { roundHours, roundUpToNearestMinute } from '../../store/utils/worklogUtils'

function transformSettlementResponses(
  responses: SettlementResponse[],
  maxTotalHoursInGivenPeriod: number
): Settlement[] {
  return responses.map(response => {
    const roundedTotalHoursWorkedOrPlanned = roundHours(response.totalHoursWorkedOrPlanned)
    const roundedTotalOperatingHours = roundHours(response.totalOperatingHours)

    const totalHoursInGivenPeriod = (maxTotalHoursInGivenPeriod * response.employmentPercentage) / 100
    const totalMissingHours = maxTotalHoursInGivenPeriod - roundedTotalHoursWorkedOrPlanned
    const percentageHoursWorked = (roundedTotalHoursWorkedOrPlanned / maxTotalHoursInGivenPeriod) * 100
    const percentageOperatingHours = (roundedTotalOperatingHours / maxTotalHoursInGivenPeriod) * 100

    const workHoursAsWorkEntries: WorkEntry[] = response.workHours.map(workHoursSummedEntry => ({
      id: workHoursSummedEntry.id,
      hours: roundHours(workHoursSummedEntry.hours),
      project: workHoursSummedEntry.project,
      description: '',
      remote: false,
      fromImport: false,
      version: 0,
      paidVacation: workHoursSummedEntry.paidVacation ?? null,
    }))

    return {
      employee: response.employee,
      employmentPercentage: parseFloat(response.employmentPercentage.toFixed(1)),
      totalHoursInGivenPeriod: parseFloat(totalHoursInGivenPeriod.toFixed(1)),
      totalHoursWorkedOrPlanned: parseFloat(roundedTotalHoursWorkedOrPlanned.toFixed(1)),
      totalOperatingHours: parseFloat(roundedTotalOperatingHours.toFixed(1)),
      totalNonOperatingHours: parseFloat(response.totalNonOperatingHours.toFixed(1)),
      totalMissingOrFreeHours: parseFloat(totalMissingHours.toFixed(1)),
      percentageHoursWorkedOrPlanned: parseFloat(percentageHoursWorked.toFixed(1)),
      percentageOperatingHours: parseFloat(percentageOperatingHours.toFixed(1)),
      workHoursOrSchedule: workHoursAsWorkEntries,
    }
  })
}

type CompletedTableProps = {
  workHoursInGivenPeriod: number
  leaderId: string
  from: string
  to: string
  renderFooter: (settlements: Settlement[]) => ReactNode
  searchText: string
  employmentTypeFilter: string | undefined
}

const CompletedTable = ({
  workHoursInGivenPeriod,
  leaderId,
  from,
  to,
  renderFooter,
  searchText,
  employmentTypeFilter,
}: CompletedTableProps) => {
  const { data } = useGetCompletedSettlementsQuery({ id: leaderId, props: { fromDate: from, toDate: to } })

  const settlements = useMemo(() => {
    if (!data) return []

    const trimmedSearchText = searchText.trim().toLowerCase()
    const hasSearchText = !!trimmedSearchText
    const hasEmploymentFilter = !!employmentTypeFilter

    return transformSettlementResponses(data, workHoursInGivenPeriod).filter(value => {
      const { firstName, lastName, employmentType } = value.employee

      const matchesSearchText =
        !hasSearchText ||
        firstName.toLowerCase().includes(trimmedSearchText) ||
        lastName.toLowerCase().includes(trimmedSearchText)

      let matchesEmploymentType = !hasEmploymentFilter
      if (employmentType !== null) {
        matchesEmploymentType = !hasEmploymentFilter || employmentTypeFilter.includes(employmentType.value)
      }

      return matchesSearchText && matchesEmploymentType
    })
  }, [data, workHoursInGivenPeriod, searchText, employmentTypeFilter])

  const columns: Column[] = [
    { id: 'employee', label: t('page.settlements.table.headers.employee') },
    { id: 'employmentPercentage', label: t('page.settlements.table.headers.fte') },
    { id: 'totalHoursInGivenPeriod', label: t('page.settlements.table.headers.totalHoursInGivenPeriod') },
    { id: 'totalHoursWorked', label: t('page.settlements.table.headers.totalHoursWorked') },
    { id: 'totalOperatingHours', label: t('page.settlements.table.headers.totalOperatingHours') },
    { id: 'totalNonOperatingHours', label: t('page.settlements.table.headers.totalNonOperatingHours') },
    { id: 'totalMissingHours', label: t('page.settlements.table.headers.totalMissingHours') },
    { id: 'percentageHoursWorked', label: t('page.settlements.table.headers.percentageHoursWorked') },
    { id: 'percentageOperatingHours', label: t('page.settlements.table.headers.percentageOperatingHours') },
  ]

  const cellSx = { px: 1 }

  const renderRow = (row: CollapsableRow) => {
    const getSrc = () => {
      if (row.employee !== null && row.employee.avatar !== null && row.employee.extension !== 'LINK') {
        return 'data:image/png;base64,' + row.employee.avatar
      } else if (row.employee !== null && row.employee.avatar !== null && row.employee.extension === 'LINK') {
        return atob(row.employee.avatar !== undefined ? row.employee.avatar : '')
      } else {
        return ''
      }
    }

    const src = getSrc()

    const getColor = () => {
      return row.totalMissingOrFreeHours > 0
        ? 'warning.main'
        : row.totalMissingOrFreeHours < 0
        ? 'accent.green.main'
        : 'auto'
    }

    return (
      <>
        <TableCell align="left" sx={{ ...cellSx, pl: 0 }}>
          <Box display="flex" gap="4px" alignItems="center" sx={{ whiteSpace: 'nowrap' }}>
            <Avatar alt={row.employee.firstName} src={src} sx={{ width: 24, height: 24, flexShrink: 0 }} />
            {row.employee.firstName + ' ' + row.employee.lastName}
          </Box>
        </TableCell>
        <TableCell align="center" sx={cellSx}>{`${row.employmentPercentage}`}</TableCell>
        <TableCell align="center" sx={cellSx}>{`${row.totalHoursInGivenPeriod}`}</TableCell>
        <TableCell align="center" sx={cellSx}>{`${row.totalHoursWorkedOrPlanned}`}</TableCell>
        <TableCell align="center" sx={cellSx}>{`${row.totalOperatingHours}`}</TableCell>
        <TableCell align="center" sx={cellSx}>{`${row.totalNonOperatingHours}`}</TableCell>
        <TableCell
          align="center"
          sx={{ ...cellSx, color: getColor(), fontWeight: 'bold' }}
        >{`${row.totalMissingOrFreeHours}`}</TableCell>
        <TableCell
          align="center"
          sx={{ ...cellSx, color: getColor(), fontWeight: 'bold' }}
        >{`${row.percentageHoursWorkedOrPlanned}%`}</TableCell>
        <TableCell align="center" sx={cellSx}>{`${row.percentageOperatingHours}%`}</TableCell>
      </>
    )
  }

  const renderCollapsedRow = (row: Row, open: boolean) => {
    const workHours: WorkEntry[] = row.workHours

    const getWorkEntryName = (workEntry: WorkEntry) => {
      if (workEntry.project.name !== 'Urlop') {
        return workEntry.project.name
      }

      const workEntryWithPaidVacation = workEntry as WorkEntry & { paidVacation?: boolean | null }
      if (workEntryWithPaidVacation.paidVacation === true) {
        return t('page.settlements.table.paidVacation')
      }
      if (workEntryWithPaidVacation.paidVacation === false) {
        return t('page.settlements.table.unpaidVacation')
      }
      return t('page.settlements.table.vacation')
    }

    return (
      <>
        <TransitionTableCell sx={{ padding: '0px', minWidth: '48px' }} open={open} />
        <TransitionTableCell open={open} />
        <TransitionTableCell open={open} sx={{ verticalAlign: 'top' }} colSpan={2}>
          <Typography fontWeight={700} sx={{ lineHeight: 'inherit' }}>
            {t('page.settlements.table.operationalProjects')}:
          </Typography>
          {workHours
            .filter(value => value.project.operational)
            .map(value => (
              <Stack
                direction={'row'}
                sx={{ justifyContent: 'space-between', gap: 0.5, alignItems: 'center' }}
                key={value.id}
              >
                <Typography sx={{ lineHeight: 'inherit' }}>{`${getWorkEntryName(value)}`}</Typography>
                <Typography sx={{ lineHeight: 'inherit' }}>{`${roundUpToNearestMinute(value.hours)}h`}</Typography>
              </Stack>
            ))}
        </TransitionTableCell>
        <TransitionTableCell open={open} sx={{ verticalAlign: 'top' }} colSpan={2}>
          <Typography fontWeight={700} sx={{ lineHeight: 'inherit' }}>
            {t('page.settlements.table.nonOperationalProjects')}:
          </Typography>
          {workHours
            .filter(
              value =>
                !value.project.operational && !(value.project.name === 'Urlop' || value.project.name === 'Nadgodziny')
            )
            .map(value => (
              <Stack
                direction={'row'}
                sx={{ justifyContent: 'space-between', gap: 0.5, alignItems: 'center' }}
                key={value.id}
              >
                <Typography sx={{ lineHeight: 'inherit' }}>{`${getWorkEntryName(value)}`}</Typography>
                <Typography sx={{ lineHeight: 'inherit' }}>{`${roundUpToNearestMinute(value.hours)}h`}</Typography>
              </Stack>
            ))}
        </TransitionTableCell>
        <TransitionTableCell sx={{ verticalAlign: 'top' }} open={open}>
          {workHours
            .filter(value => value.project.name === 'Urlop' || value.project.name === 'Nadgodziny')
            .map(value => (
              <Stack
                direction={'row'}
                sx={{ justifyContent: 'space-between', gap: 0.5, alignItems: 'center' }}
                key={value.id}
              >
                <Typography sx={{ lineHeight: 'inherit' }}>{`${getWorkEntryName(value)}`}</Typography>
                <Typography sx={{ lineHeight: 'inherit' }}>{`${roundUpToNearestMinute(value.hours)}h`}</Typography>
              </Stack>
            ))}
        </TransitionTableCell>
        <TransitionTableCell open={open} colSpan={3}></TransitionTableCell>
      </>
    )
  }

  const collapsableRows: CollapsableRow[] = settlements.map((row, index) => ({
    ...row,
    index: index,
    hasCollapsedData: row.workHoursOrSchedule.length > 0,
    collapsedData: [
      {
        id: 'workHours-' + row.employee.id,
        workHours: row.workHoursOrSchedule,
        employmentPercentage: row.employmentPercentage,
      },
    ],
  }))

  return (
    <Stack sx={{ flex: 1, minHeight: 0 }}>
      <CollapsibleTable
        columns={columns}
        collapsableRows={collapsableRows}
        renderRow={renderRow}
        renderCollapsedRow={renderCollapsedRow}
        renderFooter={() => renderFooter(settlements)}
        tableContainerSxProps={{
          scrollbarWidth: undefined,
          '&::-webkit-scrollbar': { display: undefined },
          '@supports (-moz-appearance:none)': { scrollbarWidth: undefined },
          maxHeight: undefined,
        }}
      />
    </Stack>
  )
}

export default CompletedTable
